import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {
  Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress,
  IconButton, Tooltip, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNotify } from 'react-admin';
import { downloadArtFile } from '../lib/download';
import { AppArtFile } from '../types';

/** Xadrez: sem isso não dá para ver que a logo tem fundo branco em vez de alpha. */
const CHECKERBOARD = {
  backgroundImage:
    'linear-gradient(45deg, #e0e0e0 25%, transparent 25%), linear-gradient(-45deg, #e0e0e0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e0e0e0 75%), linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
  backgroundColor: '#fafafa',
};

const formatSize = (bytes: number | null) => {
  if (!bytes) { return null; }
  if (bytes < 1024 * 1024) { return `${Math.round(bytes / 1024)} KB`; }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface Props {
  file: AppArtFile;
  /** Habilita a ação de eleger (logo principal / modelo escolhido). */
  showPrimary?: boolean;
  /** Texto da ação, por bucket — "Escolher este modelo" pesa mais que uma ★. */
  primaryLabel?: string;
  /** Texto quando já é o eleito. */
  primaryActiveLabel?: string;
  /** Modelo é a folha de mockup (duas telas lado a lado) — card maior. */
  tall?: boolean;
  readOnly?: boolean;
  busy?: boolean;
  onOpen?: (file: AppArtFile) => void;
  /** Abre o mockup ao vivo deste modelo. Ausente, o botão não aparece. */
  onOpenMockup?: () => void;
  /** Compõe o QR e o código sobre o gabarito. Ausente, o botão não aparece. */
  onOpenPlate?: () => void;
  /** Monta o comando de ajuste deste modelo. Ausente, o botão não aparece. */
  onAdjust?: () => void;
  onSetPrimary?: (file: AppArtFile) => void;
  onRemove?: (file: AppArtFile) => void;
}

const AssetCard = ({
  file, showPrimary, primaryLabel = 'Usar como principal',
  primaryActiveLabel = 'Principal', tall, readOnly, busy,
  onOpen, onOpenMockup, onOpenPlate, onAdjust, onSetPrimary, onRemove,
}: Props) => {
  const notify = useNotify();
  const [downloading, setDownloading] = useState(false);

  const preview = file.previewUrl || (file.mimeType?.startsWith('image/') ? file.fileUrl : null);
  const warnings = file.warnings || [];
  const dimensions = file.width && file.height ? `${file.width}×${file.height}` : null;
  const size = formatSize(file.byteSize);

  const download = async () => {
    setDownloading(true);
    try {
      await downloadArtFile(file);
    } catch (error: any) {
      notify(error?.message || 'Não foi possível baixar o arquivo', { type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        width: tall ? 360 : 168,
        position: 'relative',
        borderColor: file.isPrimary ? 'success.main' : undefined,
        borderWidth: file.isPrimary ? 2 : 1,
        opacity: busy ? 0.6 : 1,
      }}
    >
      <CardActionArea
        onClick={() => onOpen?.(file)}
        sx={{
          height: tall ? 360 : 132, ...CHECKERBOARD,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          '&:hover .zoom-hint': { opacity: 1 },
        }}
      >
        <ZoomInIcon
          className="zoom-hint"
          sx={{
            position: 'absolute', bottom: 8, right: 8, color: '#fff', opacity: 0,
            backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: '50%', p: 0.25,
            transition: 'opacity 120ms',
          }}
        />
        {preview ? (
          <Box
            component="img"
            src={preview}
            alt={file.fileName || ''}
            sx={{ maxHeight: tall ? 352 : 124, maxWidth: tall ? 348 : 156, objectFit: 'contain' }}
          />
        ) : (
          // Formato sem preview possível (CDR, EPS binário). O arquivo continua
          // guardado — material sem preview ainda é melhor que material nenhum.
          <Box sx={{ textAlign: 'center', color: 'text.secondary', px: 1 }}>
            <InsertDriveFileOutlinedIcon fontSize="large" />
            <Typography variant="caption" display="block">
              {(file.fileName?.split('.').pop() || 'arquivo').toUpperCase()}
            </Typography>
          </Box>
        )}
      </CardActionArea>

      {busy && (
        <Box sx={{ position: 'absolute', top: 52, left: 72 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Nada é apagado num reenvio, então o que separa as rodadas é este
          selo — sem ele os mockups novos se perdem no meio dos velhos. */}
      {!!file.generationRound && (
        <Chip
          size="small"
          label={`Rodada ${file.generationRound}`}
          sx={{
            position: 'absolute', top: 8, left: 8, height: 20,
            backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff',
            '& .MuiChip-label': { px: 0.75, fontSize: 11 },
          }}
        />
      )}

      {file.isPrimary && (
        <Chip
          size="small"
          color="success"
          icon={<CheckCircleIcon />}
          label={primaryActiveLabel}
          sx={{ position: 'absolute', top: 8, right: 8, height: 22 }}
        />
      )}

      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Tooltip title={file.fileName || ''}>
          <Typography variant="caption" noWrap display="block" sx={{ fontWeight: 500 }}>
            {file.fileName || 'sem nome'}
          </Typography>
        </Tooltip>
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {[dimensions, size].filter(Boolean).join(' · ') || '—'}
        </Typography>

        {!!warnings.length && (
          <Tooltip title={<Box component="ul" sx={{ m: 0, pl: 2 }}>{warnings.map(w => <li key={w}>{w}</li>)}</Box>}>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<WarningAmberIcon />}
              label={warnings.length === 1 ? '1 aviso' : `${warnings.length} avisos`}
              sx={{ mt: 0.5, height: 20, '& .MuiChip-label': { px: 0.5, fontSize: 11 } }}
            />
          </Tooltip>
        )}

        {/* A folha é um retrato do mockup; aqui a mesma arte abre AO VIVO, com
            o chrome do app ajustável. É onde se vê se o texto do RN sobrevive
            à foto e se a logo precisa de um fundo. */}
        {onOpenMockup && (
          <Button
            fullWidth
            size="small"
            variant="text"
            startIcon={<PhoneIphoneIcon />}
            onClick={onOpenMockup}
            sx={{ mt: 1, fontSize: 12 }}
          >
            Ver no app
          </Button>
        )}

        {/* O card mostra o gabarito com os dois espaços VAZIOS, que é o que o
            gerador entrega. Só aqui a peça aparece montada — e é onde se vê se
            o código do posto cabe na tarja antes de mandar imprimir. */}
        {onOpenPlate && (
          <Button
            fullWidth
            size="small"
            variant="text"
            startIcon={<QrCode2OutlinedIcon />}
            onClick={onOpenPlate}
            sx={{ mt: 1, fontSize: 12 }}
          >
            Compor QR e código
          </Button>
        )}

        {/* Vizinho do "Ver no app" de propósito: os dois falam DESTE modelo, e
            o ajuste é o que se pede depois de olhar a arte no aparelho. */}
        {onAdjust && (
          <Button
            fullWidth
            size="small"
            variant="text"
            startIcon={<AutoFixHighIcon />}
            onClick={onAdjust}
            sx={{ mt: 0.5, fontSize: 12 }}
          >
            Pedir ajuste
          </Button>
        )}

        {showPrimary && !readOnly && !file.isPrimary && (
          <Button
            fullWidth
            size="small"
            variant="outlined"
            startIcon={<StarBorderIcon />}
            disabled={busy}
            onClick={() => onSetPrimary?.(file)}
            sx={{ mt: 1, fontSize: 12 }}
          >
            {primaryLabel}
          </Button>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25, mt: 0.5 }}>
          <Tooltip title="Baixar original">
            <span>
              <IconButton size="small" disabled={downloading} onClick={download}>
                {downloading
                  ? <CircularProgress size={16} />
                  : <DownloadIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remover">
            <span>
              <IconButton size="small" disabled={readOnly || busy} onClick={() => onRemove?.(file)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AssetCard;
